import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  status: number
}

interface User {
  id: number
  name: string
}

interface AssignmentData {
  id: number
  gps_device_name: string | null
  installed_at: string | null
  installed_by: string | null
  install_vehicle: string | null
  install_gps_location: string | null
  install_vehicle_location: string | null
  removed_by_id: number | null
  removed_at: string | null
  remove_vehicle_location: string
}

interface Props {
  case_record: CaseRecord
  assignment: AssignmentData
  users: User[]
  errors?: Record<string, string[]>
}

export default function Remove({
  case_record,
  assignment,
  users,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "GPS", href: `/cases/${case_record.id}/gps` },
    {
      title: "Remove",
      href: `/cases/${case_record.id}/gps/${assignment.id}/remove`,
    },
  ]

  const { data, setData, patch, processing } = useForm({
    removed_by_id: assignment.removed_by_id,
    removed_at: assignment.removed_at,
    remove_vehicle_location: assignment.remove_vehicle_location,
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    patch(`/cases/${case_record.id}/gps/${assignment.id}/remove`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Remove GPS - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Record GPS Removal</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          {/* Installation Details (read-only) */}
          <div className="mb-6">
            <h3 className="mb-3 text-lg font-medium">GPS Installation</h3>
            <div className="bg-hpi-pale-gray grid gap-4 rounded-md p-4 md:grid-cols-2">
              <div>
                <span className="text-hpi-med-gray text-sm">Unit</span>
                <p className="font-medium">{assignment.gps_device_name}</p>
              </div>
              <div>
                <span className="text-hpi-med-gray text-sm">Installed On</span>
                <p className="font-medium">{assignment.installed_at}</p>
              </div>
              <div>
                <span className="text-hpi-med-gray text-sm">Installed By</span>
                <p className="font-medium">{assignment.installed_by}</p>
              </div>
              <div>
                <span className="text-hpi-med-gray text-sm">Vehicle</span>
                <p className="font-medium">{assignment.install_vehicle}</p>
              </div>
              <div>
                <span className="text-hpi-med-gray text-sm">
                  Placement on Vehicle
                </span>
                <p className="font-medium">{assignment.install_gps_location}</p>
              </div>
              <div>
                <span className="text-hpi-med-gray text-sm">
                  Location of Vehicle when GPS Installed
                </span>
                <p className="font-medium">
                  {assignment.install_vehicle_location}
                </p>
              </div>
            </div>
          </div>

          {/* Removal Form */}
          <form onSubmit={submit} className="space-y-6">
            <h3 className="text-lg font-medium">GPS Removal</h3>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className={labelClass}>Removal Date & Time</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={data.removed_at ?? ""}
                  onChange={(e) => setData("removed_at", e.target.value)}
                  required
                />
                {errors?.removed_at && (
                  <p className={errorClass}>{errors.removed_at[0]}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>Removed By</label>
                <select
                  className={inputClass}
                  value={data.removed_by_id ?? ""}
                  onChange={(e) =>
                    setData(
                      "removed_by_id",
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  required
                >
                  <option value="">Select employee...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {errors?.removed_by_id && (
                  <p className={errorClass}>{errors.removed_by_id[0]}</p>
                )}
                {errors?.removed_by && (
                  <p className={errorClass}>{errors.removed_by[0]}</p>
                )}
              </div>
            </div>

            <div>
              <label className={labelClass}>
                Location of Vehicle when GPS Removed
              </label>
              <textarea
                className={inputClass}
                rows={4}
                value={data.remove_vehicle_location}
                onChange={(e) =>
                  setData("remove_vehicle_location", e.target.value)
                }
                placeholder="Address or description of where the vehicle was located when GPS was removed"
                required
              />
              {errors?.remove_vehicle_location && (
                <p className={errorClass}>
                  {errors.remove_vehicle_location[0]}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Link
                href={`/cases/${case_record.id}/gps`}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-red hover:bg-hpi-red/90 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Record Removal"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
